import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, call } from "@/core/api/client";
import { ENDPOINTS } from "@/core/api/endpoints";
import { QUERY_KEYS } from "@/core/api/query-client";
import type { components } from "@/core/api/schema";

import type { Brand, BrandStatus } from "./brand-display";
import type { MealSlotBody, MealSlotChanges } from "./meal-slot-form";

export type CreateBrandInput = components["schemas"]["CreateBrandRequest"];
export type UpdateBrandInput = components["schemas"]["UpdateBrandRequest"];

/** Rows per page of the brand list. */
export const BRAND_PAGE_SIZE = 25;

export interface BrandFilters {
  search?: string;
  status?: BrandStatus;
}

export function useBrandList(filters: BrandFilters) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.brands.list(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      call(
        api.GET(ENDPOINTS.adminBrands, {
          params: {
            query: {
              limit: BRAND_PAGE_SIZE,
              ...(pageParam ? { cursor: pageParam } : {}),
              ...(filters.search ? { search: filters.search } : {}),
              ...(filters.status ? { status: filters.status } : {}),
            },
          },
        }),
      ),
    getNextPageParam: (page) => page.meta.nextCursor ?? undefined,
  });
}

export function useBrand(brandId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.brands.detail(brandId),
    queryFn: async () => (await call(api.GET(ENDPOINTS.adminBrand, { params: { path: { brandId } } }))).data,
  });
}

/** After a change: the brand's own page from the response, every list and the overview refetched. */
function useBrandChanged() {
  const queryClient = useQueryClient();
  return (brand: Brand) => {
    queryClient.setQueryData(QUERY_KEYS.brands.detail(brand.id), (current: object | undefined) =>
      current ? { ...current, ...brand } : current,
    );
    void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.brands.all });
    void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.overview });
  };
}

export function useCreateBrand() {
  const changed = useBrandChanged();
  return useMutation({
    mutationFn: async (input: CreateBrandInput) =>
      (await call(api.POST(ENDPOINTS.adminBrands, { body: input }))).data,
    onSuccess: changed,
  });
}

export function useUpdateBrand(brandId: string) {
  const changed = useBrandChanged();
  return useMutation({
    mutationFn: async (input: UpdateBrandInput) =>
      (await call(api.PATCH(ENDPOINTS.adminBrand, { params: { path: { brandId } }, body: input }))).data,
    onSuccess: changed,
  });
}

export function useChangeBrandStatus(brandId: string) {
  const changed = useBrandChanged();
  return useMutation({
    mutationFn: async (input: { status: BrandStatus; reason: string }) =>
      (await call(api.POST(ENDPOINTS.adminBrandStatus, { params: { path: { brandId } }, body: input }))).data,
    onSuccess: changed,
  });
}

// ───────────── Meal slots (part of the brand page, which lists them) ─────────────

/** After a slot change: the brand page refetched (it carries the slots). */
function useMealSlotsChanged(brandId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.brands.detail(brandId) });
}

export function useAddMealSlot(brandId: string) {
  const changed = useMealSlotsChanged(brandId);
  return useMutation({
    mutationFn: async (input: MealSlotBody) =>
      (await call(api.POST(ENDPOINTS.adminBrandMealSlots, { params: { path: { brandId } }, body: input })))
        .data,
    onSuccess: changed,
  });
}

export function useUpdateMealSlot(brandId: string, slotId: string) {
  const changed = useMealSlotsChanged(brandId);
  return useMutation({
    mutationFn: async (input: MealSlotChanges) =>
      (
        await call(
          api.PATCH(ENDPOINTS.adminBrandMealSlot, { params: { path: { brandId, slotId } }, body: input }),
        )
      ).data,
    onSuccess: changed,
  });
}

export function useReorderMealSlots(brandId: string) {
  const changed = useMealSlotsChanged(brandId);
  return useMutation({
    mutationFn: async (slotIds: string[]) =>
      (
        await call(
          api.PUT(ENDPOINTS.adminBrandMealSlotOrder, { params: { path: { brandId } }, body: { slotIds } }),
        )
      ).data,
    onSuccess: changed,
  });
}

// ───────────── Team and terms (part of the brand page) ─────────────

export type NewCommissionTermInput = components["schemas"]["NewCommissionTermRequest"];
export type NewPayoutPolicyInput = components["schemas"]["NewPayoutPolicyRequest"];

export function useBrandTeam(brandId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.brands.team(brandId),
    queryFn: async () =>
      (await call(api.GET(ENDPOINTS.adminBrandTeam, { params: { path: { brandId } } }))).data,
  });
}

export function useInviteOwner(brandId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (email: string) =>
      (
        await call(
          api.POST(ENDPOINTS.adminBrandOwnerInvitation, { params: { path: { brandId } }, body: { email } }),
        )
      ).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.brands.team(brandId) }),
  });
}

export function useCommissionTerms(brandId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.brands.commissionTerms(brandId),
    queryFn: async () =>
      (await call(api.GET(ENDPOINTS.adminBrandCommissionTerms, { params: { path: { brandId } } }))).data,
  });
}

export function useStartCommissionTerm(brandId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewCommissionTermInput) =>
      (
        await call(
          api.POST(ENDPOINTS.adminBrandCommissionTerms, { params: { path: { brandId } }, body: input }),
        )
      ).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.brands.commissionTerms(brandId) }),
  });
}

export function usePayoutPolicies(brandId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.brands.payoutPolicies(brandId),
    queryFn: async () =>
      (await call(api.GET(ENDPOINTS.adminBrandPayoutPolicies, { params: { path: { brandId } } }))).data,
  });
}

export function useStartPayoutPolicy(brandId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewPayoutPolicyInput) =>
      (
        await call(
          api.POST(ENDPOINTS.adminBrandPayoutPolicies, { params: { path: { brandId } }, body: input }),
        )
      ).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.brands.payoutPolicies(brandId) }),
  });
}

/** Platform settings (for the defaults a brand without its own terms gets). Changes rarely: fresh 5 min. */
export function usePlatformSettings() {
  return useQuery({
    queryKey: QUERY_KEYS.settings,
    queryFn: async () => (await call(api.GET(ENDPOINTS.adminSettings))).data,
    staleTime: 5 * 60_000,
  });
}
